import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, Trophy, Clock, ShieldAlert } from 'lucide-react';

/**
 * Round 16 — team-specific verdict chip + disruption-aware result card.
 *
 * The chip is driven by `effective_verdict` × `prediction_strength`:
 *   MATCH_QUERENT  + clear    → green : "<QUERENT> WINS"
 *   MATCH_QUERENT  + narrow   → amber : "<QUERENT> EDGES IT"
 *   MATCH_QUERENT  + marginal → amber : "SLIGHT <QUERENT> LEAN"
 *   MATCH_OPPONENT + clear    → red   : "<OPPONENT> WINS"
 *   MATCH_OPPONENT + narrow   → amber : "<OPPONENT> EDGES IT"
 *   MATCH_OPPONENT + marginal → amber : "SLIGHT <OPPONENT> LEAN"
 *   MATCH_DISRUPTED           → grey  : "NO RESULT LIKELY"
 *
 * Confidence percentage is intentionally NOT rendered anywhere — the chip +
 * prediction_strength already communicate certainty. The RCB regression
 * proved showing "45% confidence" next to "YES" contradicted itself.
 */

const CHIP_STYLE = {
    green: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40',
    red:   'bg-rose-500/15 text-rose-300 border-rose-500/40',
    amber: 'bg-amber-500/15 text-amber-300 border-amber-500/40',
    grey:  'bg-slate-500/15 text-slate-300 border-slate-500/40',
};

function chipFor(q) {
    const ev = q.effective_verdict;
    const strength = q.prediction_strength || 'clear';
    const querent = q.querent_team || 'YOUR TEAM';
    const opponent = q.opponent_team || 'OPPONENT';
    const upper = (s) => String(s).toUpperCase();

    if (ev === 'MATCH_DISRUPTED') {
        return { label: 'NO RESULT LIKELY', tone: 'grey' };
    }
    if (ev === 'MATCH_QUERENT' || ev === 'MATCH_OPPONENT') {
        const name = upper(ev === 'MATCH_QUERENT' ? querent : opponent);
        if (strength === 'clear') {
            return { label: `${name} WINS`, tone: ev === 'MATCH_QUERENT' ? 'green' : 'red' };
        }
        if (strength === 'narrow') {
            return { label: `${name} EDGES IT`, tone: 'amber' };
        }
        if (strength === 'marginal') {
            return { label: `SLIGHT ${name} LEAN`, tone: 'amber' };
        }
        if (strength === 'disrupted') {
            // Defensive: pipeline should have set effective_verdict=MATCH_DISRUPTED.
            return { label: 'NO RESULT LIKELY', tone: 'grey' };
        }
        return { label: `${name} WINS`, tone: ev === 'MATCH_QUERENT' ? 'green' : 'red' };
    }

    // Defensive — is_match_prediction was true but pipeline returned a
    // non-MATCH effective_verdict (legacy YES/NO/MATCH_DRAW). Log so the
    // regression is caught; fall back to no-result.
    // eslint-disable-next-line no-console
    console.error(
        '[MatchVerdictChip] is_match_prediction is true but effective_verdict is',
        ev,
        '— falling back to NO RESULT LIKELY. Expected MATCH_QUERENT / MATCH_OPPONENT / MATCH_DISRUPTED.'
    );
    return { label: 'NO RESULT LIKELY', tone: 'grey' };
}

export function MatchVerdictChip({ q }) {
    const { label, tone } = chipFor(q);
    return (
        <Badge
            data-testid="match-verdict-chip"
            data-strength={q.prediction_strength || ''}
            data-effective-verdict={q.effective_verdict || ''}
            className={`rounded-sm border tracking-wider uppercase ${CHIP_STYLE[tone]}`}
        >
            {label}
        </Badge>
    );
}

export default function MatchResultCard({ q }) {
    const ev = q.effective_verdict;
    const strength = q.prediction_strength || 'clear';
    const querentTeam = q.querent_team || 'Your side';
    const opponentTeam = q.opponent_team || 'Opponent';
    const disruptionRisk = !!q.match_disruption_risk;
    const disruptionLabel = q.match_disruption_label;
    const topSignal = (q.match_disruption_signals || [])[0];
    const matchTiming = q.match_timing;

    // DISRUPTED — no winner named, just the disruption framing.
    if (ev === 'MATCH_DISRUPTED') {
        return (
            <Card
                data-testid="match-disrupted-card"
                className="rounded-sm p-6 border bg-amber-500/5 border-amber-500/30"
            >
                <div className="flex items-start gap-3">
                    <AlertTriangle size={18} className="text-amber-300 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                        <div className="text-amber-200 font-medium leading-snug" data-testid="match-disrupted-label">
                            {disruptionLabel || 'Contest disrupted — external factor may prevent a clean outcome.'}
                        </div>
                        {topSignal?.meaning && (
                            <div className="mt-2 text-xs text-slate-400 leading-relaxed" data-testid="match-disrupted-signal">
                                {topSignal.meaning}
                            </div>
                        )}
                    </div>
                </div>
            </Card>
        );
    }

    // QUERENT or OPPONENT winner — card varies by prediction_strength.
    const isQuerentWin = ev === 'MATCH_QUERENT';
    const winnerName = isQuerentWin ? querentTeam : opponentTeam;
    // Lagna-arc time only for a CLEAR querent win with no disruption.
    const showTiming =
        isQuerentWin &&
        strength === 'clear' &&
        !disruptionRisk &&
        matchTiming?.predicted_ist;

    // Narrow / marginal → amber card with score-gap explanation.
    const cardTone =
        strength === 'narrow' || strength === 'marginal'
            ? 'bg-amber-500/5 border-amber-500/30'
            : isQuerentWin
                ? 'bg-emerald-500/5 border-emerald-500/30'
                : 'bg-rose-500/5 border-rose-500/30';

    const headerLabel =
        strength === 'clear' ? 'Predicted winner' :
        strength === 'narrow' ? 'Narrow edge' :
        strength === 'marginal' ? 'Marginal lean' : 'Predicted winner';

    const qScore = q.match_result?.querent_score ?? null;
    const oScore = q.match_result?.opponent_score ?? null;

    return (
        <Card
            data-testid="match-winner-card"
            data-strength={strength}
            className={`rounded-sm p-6 border ${cardTone}`}
        >
            <div className="flex items-start gap-3 mb-2">
                <Trophy size={18} className="text-marigold mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                    <div className="text-xs uppercase tracking-widest text-slate-400 mb-1">
                        {headerLabel}
                    </div>
                    <div className="text-parchment font-serif text-xl leading-tight" data-testid="match-winner-name">
                        {winnerName}
                    </div>
                </div>
            </div>

            {/* Score-gap explanation for narrow / marginal predictions */}
            {(strength === 'narrow' || strength === 'marginal') && qScore != null && oScore != null && (
                <div
                    data-testid="match-score-gap"
                    className="mt-3 pt-3 border-t border-amber-500/20 text-sm text-parchment"
                >
                    <span className="text-slate-400">Planetary scores: </span>
                    <span className="font-medium">{querentTeam} {qScore}</span>
                    <span className="text-slate-500"> · </span>
                    <span className="font-medium">{opponentTeam} {oScore}</span>
                    <div className="text-xs text-slate-500 mt-1 leading-relaxed">
                        {strength === 'marginal'
                            ? 'Scores are equal — the asking side gets the marginal edge. Treat as genuinely uncertain.'
                            : `A margin of ${Math.abs(qScore - oScore)} point(s). A single event could flip this.`}
                    </div>
                </div>
            )}

            {/* Lagna arc — only for CLEAR querent win without disruption */}
            {showTiming && (
                <div
                    data-testid="match-timing-row"
                    className="mt-4 pt-4 border-t border-marigold/10 flex items-center gap-2 text-sm text-parchment"
                >
                    <Clock size={14} className="text-marigold" />
                    <div>
                        <span className="text-slate-400">Predicted activation: </span>
                        <span className="text-parchment font-medium">{matchTiming.predicted_ist} IST</span>
                        <div className="text-xs text-slate-500 mt-0.5">
                            Based on lagna arc at moment of question
                        </div>
                    </div>
                </div>
            )}

            {/* Disruption caveat on a named winner */}
            {disruptionRisk && (
                <div
                    data-testid="match-disruption-warning"
                    className="mt-4 pt-4 border-t border-amber-500/20 flex items-start gap-2"
                >
                    <ShieldAlert size={14} className="text-amber-300 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-amber-200 leading-relaxed">
                        <span className="font-medium">Disruption risk detected — </span>
                        {disruptionLabel}
                        {topSignal?.meaning && (
                            <div className="text-xs text-slate-400 mt-1">{topSignal.meaning}</div>
                        )}
                    </div>
                </div>
            )}
        </Card>
    );
}
